import React, { useState } from 'react';
import { Auth } from '@aws-amplify/auth';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  LightMode,
  Stack,
  useColorModeValue as mode
} from '@chakra-ui/react';
import { UnderlineLink } from '../chakraUIPro/UnderlineLink';
import { useAnalytics } from '../../contexts/AnalyticsContext';

type LoginFormProps = {
  // TODO:
  // eslint-disable-next-line no-unused-vars
  callback(err: string | null, user?: { username: string; password: string }): void;
};

function SignInForm({ callback }: LoginFormProps) {
  const [error, setError] = useState('');
  const { logEvent } = useAnalytics();

  // eslint-disable-next-line consistent-return
  const login = async (evt: any | undefined) => {
    setError('');
    const { email: username, password } = evt;
    try {
      const user = await Auth.signIn(username, password);
      // eslint-disable-next-line no-console
      console.info({ action: 'login:resp', data: user });
      // Forced password reset on new account login
      if (user.challengeName) {
        return callback(user.challengeName, user);
      }

      logEvent({
        action: 'Signed in',
        category: 'Sign in'
      });
      return callback(null, user);
    } catch (err) {
      if (err.code) {
        if (err.code === 'UserNotConfirmedException') {
          return callback(err.code, { username, password });
        }
        if (err.code === 'PasswordResetRequiredException') {
          return callback(err.code, { username, password });
        }
      }

      console.error({ action: 'login:error', error: err });
      setError(err.message);
    }
  };

  async function onForgotPassword() {
    setError('');
    callback('FORGOT_PASSWORD');
  }

  const loginSchema = yup.object({
    email: yup.string().email('Must be a valid email').required('Email is required'),
    password: yup.string().required('Password is required')
  });

  return (
    <Formik validationSchema={loginSchema} onSubmit={login} initialValues={{ email: '', password: '' }}>
      {({ isSubmitting, handleChange, handleBlur, values, touched, errors }) => (
        <Form>
          {/* Errors */}
          {error.length > 0 && (
            <Alert status='error' mb='4' borderRadius='md'>
              <AlertIcon />
              {error}
            </Alert>
          )}

          <Stack spacing='-px'>
            {/* Email */}
            <FormControl
              // TODO:
              // @ts-ignore
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.email && !!errors.email}
            >
              <FormLabel srOnly>Email</FormLabel>
              <Input
                id='email'
                size='lg'
                name='email'
                type='email'
                autoComplete='email'
                required
                placeholder='Email address'
                bg={mode('white', 'gray.700')}
                fontSize='md'
                roundedBottom='0'
                focusBorderColor='blue.300'
              />
            </FormControl>

            {/* Password */}
            <FormControl
              // TODO:
              // @ts-ignore
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.password && !!errors.password}
            >
              <FormLabel srOnly>Password</FormLabel>
              <Input
                id='password'
                name='password'
                type='password'
                autoComplete='current-password'
                required
                size='lg'
                bg={mode('white', 'gray.700')}
                fontSize='md'
                roundedTop='0'
                placeholder='Password'
                focusBorderColor='blue.300'
              />
              <FormErrorMessage>
                <>
                  {errors.email}
                  <br />
                  {errors.password}
                </>
              </FormErrorMessage>
            </FormControl>
          </Stack>

          <Flex align='center' justify='space-between' mt='8'>
            <Box />
            {/* TODO: */}
            {/* eslint-disable-next-line react/jsx-no-bind */}
            <UnderlineLink fontSize='sm' onClick={onForgotPassword}>
              Forgot Password?
            </UnderlineLink>
          </Flex>
          <LightMode>
            <Button
              size='lg'
              type='submit'
              mt='8'
              w='full'
              colorScheme='blue'
              fontSize='md'
              fontWeight='bold'
              isLoading={isSubmitting}
              isDisabled={!!errors.email || !!errors.password}
            >
              Sign in
            </Button>
          </LightMode>
        </Form>
      )}
    </Formik>
  );
}

export default SignInForm;
